import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const pageList = (cur, pages) => {
  const set = new Set([1, pages, cur - 1, cur, cur + 1].filter((n) => n >= 1 && n <= pages));
  const out = [];
  [...set].sort((a, b) => a - b).forEach((n, i, arr) => {
    if (i && n - arr[i - 1] > 1) out.push("…" + n);
    out.push(n);
  });
  return out;
};

export function Pager({ total, page, perPage, onPage, onPerPage, sizes = [25, 50, 100], testid = "pager" }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  const from = total ? (page - 1) * perPage + 1 : 0;
  const to = Math.min(total, page * perPage);
  const btn = "h-8 min-w-8 px-2 inline-flex items-center justify-center rounded-md text-sm font-mono transition-colors";
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t" style={{ borderColor: "var(--line)" }} data-testid={testid}>
      <div className="flex items-center gap-3 text-xs muted">
        <span data-testid={`${testid}-range`}>{from}–{to} of {total}</span>
        <select value={perPage} onChange={(e) => onPerPage(+e.target.value)} className="input-box !w-auto !py-1 text-xs" data-testid={`${testid}-per-page`}>
          {sizes.map((n) => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} className={`${btn} hover:bg-[var(--surface-2)] disabled:opacity-40`} data-testid={`${testid}-prev`} aria-label="Previous page">
          <ChevronLeft size={15} />
        </button>
        {pageList(page, pages).map((n) => typeof n === "string"
          ? <span key={n} className="px-1 muted text-sm">…</span>
          : (
            <button key={n} onClick={() => onPage(n)} data-testid={`${testid}-page-${n}`}
              className={`${btn} ${n === page ? "bg-[var(--ink)] text-white" : "hover:bg-[var(--surface-2)]"}`}>{n}</button>
          ))}
        <button onClick={() => onPage(page + 1)} disabled={page >= pages} className={`${btn} hover:bg-[var(--surface-2)] disabled:opacity-40`} data-testid={`${testid}-next`} aria-label="Next page">
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}

// Client-side paging for any list; `resetKey` changes (e.g. filters) jump back to page 1.
export function usePaged(items, storageKey, resetKey, defaultPer = 25, sizes = [25, 50, 100]) {
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(() => { const v = +localStorage.getItem(storageKey); return sizes.includes(v) ? v : defaultPer; });
  useEffect(() => { setPage(1); }, [resetKey]);
  const pages = Math.max(1, Math.ceil(items.length / per));
  const cur = Math.min(page, pages);
  return {
    rows: items.slice((cur - 1) * per, cur * per),
    pagerProps: {
      total: items.length, page: cur, perPage: per,
      onPage: (n) => setPage(Math.min(Math.max(1, n), pages)),
      onPerPage: (n) => { setPer(n); setPage(1); localStorage.setItem(storageKey, String(n)); },
    },
  };
}
